import React, { useEffect, useMemo, useRef } from "react";
import { observer } from "mobx-react";
import { DocumentGenerationParamsViewStore } from "../../store";
import { DocumentGenerationParamsView } from "../view/DocumentGenerationParamsView";
import { DocumentGenerationParamsUpdateStore } from "../../store";
import { DocumentGenerationParamsUpdate } from "../update/DocumentGenerationParamsUpdate";
import { useRootStore } from "../../../../store/contexts/RootStoreContext";
import { Box, CircularProgress } from "@mui/material";

export type DocumentGenerationParamsModalContentProps = {
  entityId: string;
  mode: "view" | "update";
  onClose: () => void;
};

export const DocumentGenerationParamsModalContent: React.FC<DocumentGenerationParamsModalContentProps> =
  observer(({ entityId, mode, onClose }) => {
    const rootStore = useRootStore();
    const initializedRef = useRef<string | null>(null);

    const storeInfo = useMemo(() => {
      if (mode === "update") {
        const s = new DocumentGenerationParamsUpdateStore(rootStore);
        s.isModalMode = true;
        return { store: s, type: "update" as const };
      }
      // Default to view mode
      const s = new DocumentGenerationParamsViewStore(rootStore);
      s.isModalMode = true;
      return { store: s, type: "view" as const };
    }, [rootStore, mode]);

    useEffect(() => {
      if (entityId && initializedRef.current !== entityId) {
        initializedRef.current = entityId;
        storeInfo.store.initialize({ id: entityId });
      }
    }, [entityId, storeInfo]);

    useEffect(() => {
      return () => {
        storeInfo.store.destroy();
      };
    }, [storeInfo]);

    const store = storeInfo.store;
    if (store.status === "processing" || store.status === "uninitialized") {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 200,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box sx={{ p: 2 }}>
        {storeInfo.type === "view" && (
          <DocumentGenerationParamsView
            documentGenerationParamsViewStore={
              storeInfo.store as DocumentGenerationParamsViewStore
            }
          />
        )}
        {storeInfo.type === "update" && (
          <DocumentGenerationParamsUpdate
            documentGenerationParamsUpdateStore={
              storeInfo.store as DocumentGenerationParamsUpdateStore
            }
            isModalMode
            onModalClose={onClose}
          />
        )}
      </Box>
    );
  });
